// src/components/resources/tools/generators/FakeChat/FakeChatContext.tsx

'use client'

import { createContext, useContext, useMemo, useReducer, type ReactNode } from 'react'
import { usePreviewDeferral, useCommitStaleRef } from '../shared/usePreviewDeferral'
import type {
  ChatParticipant,
  ChatMessage,
  ChatPlatform,
  ConversationMetadata,
  FakeChatAction,
  FakeChatConversation,
  FakeChatSelectors,
  FakeChatState,
  MessageStatus
} from './FakeChat.types'

// Context definitions with proper typing
const FakeChatStateContext = createContext<FakeChatState | undefined>(undefined)
const FakeChatDispatchContext = createContext<React.Dispatch<FakeChatAction> | undefined>(undefined)
const FakeChatSelectorsContext = createContext<FakeChatSelectors | undefined>(undefined)

/**
 * Preset shape exposed to consumers of `useFakeChatActions` so the
 * `resetConversation` dispatcher can reapply the variant preset
 * (iMessage / Slack / group-chat landing pages) on reset rather
 * than fall back to the umbrella's generic messaging default. The
 * Provider closes over its `initialPlatform` / `initialChannelType`
 * props and exposes them through this context; non-variant
 * mounts surface `undefined` for both, which the reset path then
 * treats as a plain `createInitialState()` reset.
 */
interface FakeChatInitialPreset {
  platform?: ChatPlatform
  channelType?: ConversationMetadata['channelType']
}
const FakeChatInitialPresetContext = createContext<FakeChatInitialPreset>({})

/**
 * Generates a unique identifier for chat entities.
 * Uses crypto.randomUUID() when available, falls back to timestamp-based ID.
 */
const createId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `fake-chat-${Math.random().toString(16).slice(2)}-${Date.now()}`
}

/**
 * Creates the initial conversation state with sample participants and messages.
 * This provides a realistic demo conversation for the fake chat generator.
 */
const createInitialConversation = (): FakeChatConversation => {
  const participants: ChatParticipant[] = [
    {
      id: 'participant-alex',
      displayName: 'Alex Chen',
      handle: '@alex',
      avatarInitials: 'AC',
      avatarColor: 'bg-indigo-500',
      role: 'member',
      isSelf: true
    },
    {
      id: 'participant-jordan',
      displayName: 'Jordan Blake',
      handle: '@jordan',
      avatarInitials: 'JB',
      avatarColor: 'bg-emerald-500',
      role: 'member'
    },
    {
      id: 'participant-maya',
      displayName: 'Maya Patel',
      handle: '@maya',
      avatarInitials: 'MP',
      avatarColor: 'bg-rose-500',
      role: 'guest'
    }
  ]

  const messages: ChatMessage[] = [
    {
      id: 'message-1',
      participantId: 'participant-maya',
      text: 'Morning team! The webinar landing page is live — feel free to skim the hero copy and let me know if anything feels off.',
      timestamp: '9:24 AM',
      alignment: 'left',
      reactions: [
        { id: 'reaction-1', emoji: '🔥', count: 6, reactedByViewer: true },
        { id: 'reaction-2', emoji: '👏', count: 3, reactedByViewer: false }
      ],
      attachments: [
        {
          id: 'attachment-1',
          type: 'file',
          title: 'Launch Toolkit.zip',
          description: 'Landing page hero, email banners, and LinkedIn carousel'
        }
      ],
      isThreaded: true,
      threadReplyCount: 5
    },
    {
      id: 'message-2',
      participantId: 'participant-jordan',
      text: 'Appreciate it! Paid is queued with refreshed copy and targeting. We will watch CPC once impressions settle.',
      timestamp: '9:27 AM',
      alignment: 'left',
      reactions: [
        { id: 'reaction-3', emoji: '👍', count: 4, reactedByViewer: true }
      ]
    },
    {
      id: 'message-3',
      participantId: 'participant-alex',
      text: 'Thanks all. I will capture key wins for the social proof deck so sales has new creative this afternoon.',
      timestamp: '9:29 AM',
      alignment: 'right',
      reactions: [],
      status: 'delivered'
    },
    {
      id: 'message-4',
      participantId: 'participant-jordan',
      text: 'Perfect — those slides helped last quarter. Appreciate the quick turnaround!',
      timestamp: '9:31 AM',
      alignment: 'left',
      reactions: [
        { id: 'reaction-4', emoji: '🙌', count: 2, reactedByViewer: false }
      ]
    }
  ]

  const metadata: ConversationMetadata = {
    id: 'conversation-default',
    platform: 'messaging',
    workspaceName: 'Campaign Hub',
    channelName: 'Launch sync',
    channelTopic: 'Status updates • Marketing + GTM',
    channelType: 'group',
    backgroundColor: 'from-slate-950 via-slate-900 to-slate-950',
    accentColor: 'indigo-500',
    showAvatars: true,
    showReactions: true,
    showTimestamps: true,
    typingIndicator: true,
    lastActivityLabel: 'Last message 2 minutes ago'
  }

  return {
    metadata,
    participants,
    messages
  }
}

/**
 * Creates the initial application state with default conversation.
 */
const createInitialState = (): FakeChatState => ({
  conversation: createInitialConversation(),
  activeMessageId: 'message-1',
  isExporting: false,
  error: null
})

/**
 * Platform-specific default configurations for different chat platforms.
 * Each platform has its own visual styling and feature set.
 */
const platformDefaults: Record<ChatPlatform, Partial<ConversationMetadata>> = {
  messaging: {
    platform: 'messaging',
    backgroundColor: 'from-slate-950 via-slate-900 to-slate-950',
    accentColor: 'indigo-500',
    channelType: 'group',
    showReactions: true,
    showAvatars: true,
    showTimestamps: true
  },
  slack: {
    platform: 'slack',
    backgroundColor: 'from-[#101418] via-[#1a1d21] to-[#0f1215]',
    accentColor: 'indigo-500',
    channelType: 'channel',
    showReactions: true,
    showAvatars: true,
    showTimestamps: true
  },
  imessage: {
    platform: 'imessage',
    backgroundColor: 'from-sky-100 via-white to-slate-100',
    accentColor: 'sky-500',
    channelType: 'dm',
    showReactions: false,
    showAvatars: false,
    showTimestamps: false
  },
  whatsapp: {
    platform: 'whatsapp',
    backgroundColor: 'from-[#efeae2] via-[#efeae2] to-[#efeae2]',
    accentColor: 'emerald-500',
    channelType: 'dm',
    // WhatsApp shows timestamps + read receipts inside every bubble; avatars
    // only matter for the group sender-name row, so keep them off for the
    // common 1:1 case (the group/channel variants flip channelType).
    showReactions: false,
    showAvatars: false,
    showTimestamps: true
  },
  messenger: {
    platform: 'messenger',
    backgroundColor: 'from-white via-white to-white',
    accentColor: 'sky-500',
    channelType: 'dm',
    // Messenger groups incoming bubbles with a small avatar; reactions render
    // as overlay pills in the real app, which we omit, so default them off.
    showReactions: false,
    showAvatars: true,
    showTimestamps: false
  },
  discord: {
    platform: 'discord',
    backgroundColor: 'from-[#313338] via-[#313338] to-[#313338]',
    accentColor: 'indigo-500',
    channelType: 'channel',
    showReactions: true,
    showAvatars: true,
    showTimestamps: true
  }
}

/**
 * Reducer function that handles all state mutations for the fake chat application.
 * Implements immutable updates and proper type safety.
 */
const fakeChatReducer = (state: FakeChatState, action: FakeChatAction): FakeChatState => {
  switch (action.type) {
    case 'SET_PLATFORM': {
      const defaults = platformDefaults[action.payload]
      return {
        ...state,
        conversation: {
          ...state.conversation,
          metadata: {
            ...state.conversation.metadata,
            ...defaults
          }
        }
      }
    }
    case 'UPDATE_METADATA': {
      return {
        ...state,
        conversation: {
          ...state.conversation,
          metadata: {
            ...state.conversation.metadata,
            ...action.payload
          }
        }
      }
    }
    case 'ADD_MESSAGE': {
      return {
        ...state,
        conversation: {
          ...state.conversation,
          messages: [...state.conversation.messages, action.payload]
        },
        activeMessageId: action.payload.id
      }
    }
    case 'UPDATE_MESSAGE': {
      return {
        ...state,
        conversation: {
          ...state.conversation,
          messages: state.conversation.messages.map(message =>
            message.id === action.payload.id ? { ...message, ...action.payload.changes } : message
          )
        }
      }
    }
    case 'REMOVE_MESSAGE': {
      const filtered = state.conversation.messages.filter(message => message.id !== action.payload)
      const lastMessage = filtered[filtered.length - 1]
      return {
        ...state,
        conversation: {
          ...state.conversation,
          messages: filtered
        },
        activeMessageId: filtered.length > 0 && lastMessage ? lastMessage.id : null
      }
    }
    case 'SET_ACTIVE_MESSAGE': {
      return {
        ...state,
        activeMessageId: action.payload
      }
    }
    case 'ADD_PARTICIPANT': {
      return {
        ...state,
        conversation: {
          ...state.conversation,
          participants: [...state.conversation.participants, action.payload]
        }
      }
    }
    case 'UPDATE_PARTICIPANT': {
      return {
        ...state,
        conversation: {
          ...state.conversation,
          participants: state.conversation.participants.map(participant =>
            participant.id === action.payload.id ? { ...participant, ...action.payload.changes } : participant
          )
        }
      }
    }
    case 'REMOVE_PARTICIPANT': {
      const participants = state.conversation.participants.filter(participant => participant.id !== action.payload)
      const messages = state.conversation.messages.filter(message => message.participantId !== action.payload)
      const lastMessage = messages[messages.length - 1]
      return {
        ...state,
        conversation: {
          ...state.conversation,
          participants,
          messages
        },
        activeMessageId: messages.length > 0 && lastMessage ? lastMessage.id : null
      }
    }
    case 'SET_EXPORTING': {
      return {
        ...state,
        isExporting: action.payload
      }
    }
    case 'SET_ERROR': {
      return {
        ...state,
        error: action.payload
      }
    }
    case 'RESET_CONVERSATION': {
      // Honour the optional preset payload so landing-page variants
      // (iMessage / Slack / group-chat) reset to their advertised
      // platform preset rather than falling back to the umbrella's
      // generic messaging styling. The Provider closes over its
      // `initialPlatform` / `initialChannelType` props and passes
      // them through `resetConversation()`.
      return applyInitialPreset(
        createInitialState(),
        action.payload?.platform,
        action.payload?.channelType,
      )
    }
    case 'TOGGLE_SIDEBAR': {
      return {
        ...state,
        conversation: {
          ...state.conversation,
          metadata: {
            ...state.conversation.metadata,
            sidebarCollapsed: !state.conversation.metadata.sidebarCollapsed
          }
        }
      }
    }
    case 'ADD_REACTION': {
      const { messageId, emoji } = action.payload
      return {
        ...state,
        conversation: {
          ...state.conversation,
          messages: state.conversation.messages.map(message => {
            if (message.id !== messageId) return message
            
            // Don't allow reactions on messages that failed to deliver
            if (message.status === 'not-delivered') return message
            
            const existingReaction = message.reactions.find(r => r.emoji === emoji)
            if (existingReaction) {
              // Increment count and mark as reacted by viewer
              return {
                ...message,
                reactions: message.reactions.map(r =>
                  r.emoji === emoji
                    ? { ...r, count: r.count + 1, reactedByViewer: true }
                    : r
                )
              }
            } else {
              // Add new reaction
              return {
                ...message,
                reactions: [
                  ...message.reactions,
                  {
                    id: `reaction-${emoji}-${Date.now()}`,
                    emoji,
                    count: 1,
                    reactedByViewer: true
                  }
                ]
              }
            }
          })
        }
      }
    }
    case 'REMOVE_REACTION': {
      const { messageId, emoji } = action.payload
      return {
        ...state,
        conversation: {
          ...state.conversation,
          messages: state.conversation.messages.map(message => {
            if (message.id !== messageId) return message
            
            return {
              ...message,
              reactions: message.reactions
                .map(r => {
                  if (r.emoji === emoji) {
                    const newCount = r.count - 1
                    return newCount > 0
                      ? { ...r, count: newCount, reactedByViewer: false }
                      : null
                  }
                  return r
                })
                .filter((r): r is NonNullable<typeof r> => r !== null)
            }
          })
        }
      }
    }
    case 'TOGGLE_REACTION': {
      const { messageId, emoji } = action.payload
      // Decide add vs remove from the LIVE reducer state — not from the
      // (possibly still-deferred) preview tree that dispatched this — so a fast
      // double-tap on a reaction pill toggles cleanly instead of incrementing
      // twice when the deferred snapshot hasn't caught up yet.
      const message = state.conversation.messages.find(m => m.id === messageId)
      const reactedByViewer = message?.reactions.find(r => r.emoji === emoji)?.reactedByViewer ?? false
      return fakeChatReducer(state, {
        type: reactedByViewer ? 'REMOVE_REACTION' : 'ADD_REACTION',
        payload: { messageId, emoji },
      })
    }
    case 'UPDATE_MESSAGE_STATUS': {
      return {
        ...state,
        conversation: {
          ...state.conversation,
          messages: state.conversation.messages.map(message =>
            message.id === action.payload.id
              ? { ...message, status: action.payload.status }
              : message
          )
        }
      }
    }
    default:
      return state
  }
}

/**
 * Props for FakeChatProvider.
 *
 * `initialPlatform` lets landing-page variants (e.g. the iMessage,
 * Slack, and group-chat variants registered in `landingVariants.ts`)
 * boot the chat generator pre-configured for a specific platform. The
 * provider applies the platform defaults synchronously inside the
 * reducer's initializer so the first render is already correct (no
 * flash of the wrong platform). The `initialChannelType` override is
 * used by the group-chat variant to force `channelType: 'group'` after
 * the platform defaults are applied — the order matters because
 * platformDefaults provides per-platform `channelType` baselines that
 * the override then narrows.
 */
export interface FakeChatProviderProps {
  children: ReactNode
  initialPlatform?: ChatPlatform
  initialChannelType?: ConversationMetadata['channelType']
}

/**
 * Apply an optional initial-preset (platform + channelType) to a
 * FakeChatState by re-using the existing SET_PLATFORM and
 * UPDATE_METADATA reducer paths. Keeps platform-defaults merging
 * logic in a single place (the reducer itself) and is consumed by
 * both the useReducer initializer (first-render preset) and the
 * RESET_CONVERSATION case (reset-preset reapplication, so the
 * "Reset preset" button on landing-page variants doesn't fall back
 * to the umbrella's generic messaging default).
 */
function applyInitialPreset(
  base: FakeChatState,
  initialPlatform: ChatPlatform | undefined,
  initialChannelType: ConversationMetadata['channelType'] | undefined,
): FakeChatState {
  if (!initialPlatform && !initialChannelType) return base
  let next = base
  if (initialPlatform) {
    next = fakeChatReducer(next, { type: 'SET_PLATFORM', payload: initialPlatform })
  }
  if (initialChannelType) {
    next = fakeChatReducer(next, {
      type: 'UPDATE_METADATA',
      payload: { channelType: initialChannelType },
    })
  }
  return next
}

/**
 * Builds the selector closures from a given state snapshot. Extracted so
 * the live Provider and the deferred preview boundary
 * (`DeferredFakeChatStateProvider`) derive selectors from whichever
 * snapshot they expose — keeping the two from ever drifting apart.
 */
function createFakeChatSelectors(state: FakeChatState): FakeChatSelectors {
  return {
    getParticipantById: (id: string) => state.conversation.participants.find(participant => participant.id === id),
    getActiveMessage: () => state.conversation.messages.find(message => message.id === state.activeMessageId),
  }
}

/**
 * Provides fake chat context to the component subtree.
 * Sets up the state management and selector functions for the entire fake chat application.
 */
export function FakeChatProvider({
  children,
  initialPlatform,
  initialChannelType,
}: FakeChatProviderProps): JSX.Element {
  const [state, dispatch] = useReducer(fakeChatReducer, undefined, () =>
    applyInitialPreset(createInitialState(), initialPlatform, initialChannelType),
  )

  // createFakeChatSelectors reads only participants/messages/activeMessageId.
  // Metadata/export/error dispatches preserve those refs — skip recompute on them.
  const selectors = useMemo<FakeChatSelectors>(
    () => createFakeChatSelectors(state),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- narrow invalidation for mobile INP
    [state.conversation.participants, state.conversation.messages, state.activeMessageId],
  )

  // Memoise the preset object so consumers of
  // FakeChatInitialPresetContext don't re-render on every Provider
  // re-render. Stable identity only matters because the values are
  // read inside useFakeChatActions' useMemo dependency list.
  const initialPreset = useMemo<FakeChatInitialPreset>(
    () => ({ platform: initialPlatform, channelType: initialChannelType }),
    [initialPlatform, initialChannelType],
  )

  return (
    <FakeChatStateContext.Provider value={state}>
      <FakeChatDispatchContext.Provider value={dispatch}>
        <FakeChatSelectorsContext.Provider value={selectors}>
          <FakeChatInitialPresetContext.Provider value={initialPreset}>
            {children}
          </FakeChatInitialPresetContext.Provider>
        </FakeChatSelectorsContext.Provider>
      </FakeChatDispatchContext.Provider>
    </FakeChatStateContext.Provider>
  )
}

/**
 * Re-provides the fake-chat state — and the selectors derived from it —
 * to its subtree from a `useDeferredValue` snapshot. Wrapping the heavy
 * `<FakeChatPreview>` (the 50KB+ multi-platform render) in this boundary
 * keeps keystrokes in the sibling Controls column responsive on mobile:
 * the input commits synchronously at high priority while the preview
 * re-renders at a lower priority from the deferred snapshot, cutting the
 * long interaction task that drove the mobile INP (~392ms in Vercel
 * Speed Insights) on the generator pages.
 *
 * Safe because the preview holds no state-bound inputs — all editing
 * happens in the Controls column — and html2canvas export reads the
 * rendered DOM on a button click, by which point the deferred value has
 * converged. The only observable effect is that, mid-typing, the preview
 * may trail the controls by a frame and then catch up; the resting
 * output is identical.
 *
 * Selectors are re-derived from the SAME deferred snapshot via the shared
 * `createFakeChatSelectors`, so consumers reading both state and
 * selectors (e.g. `useResolvedMessages`) never observe a live/deferred
 * split. Dispatch and initial-preset contexts flow through unchanged from
 * the outer `FakeChatProvider`.
 *
 * `staleRef` (optional) is set to `true` while the deferred snapshot trails
 * live state. The Controls column reads it before an html2canvas export so a
 * type-then-immediately-Download can't capture a still-pending preview frame.
 */
export function DeferredFakeChatStateProvider({
  children,
  staleRef,
}: {
  children: ReactNode
  staleRef?: React.MutableRefObject<boolean>
}): JSX.Element {
  const liveState = useFakeChatState()
  const { deferred: deferredState, isStale } = usePreviewDeferral(liveState)
  // Commit staleness into the caller-provided ref from a layout effect (pre-paint)
  // so the export gate (`waitForStable`) can't green-light a Download in the gap a
  // passive effect would leave after a keystroke. Shared with useStaleRef so the
  // semantics can't drift. See useCommitStaleRef.
  useCommitStaleRef(isStale, staleRef)
  const deferredSelectors = useMemo<FakeChatSelectors>(
    () => createFakeChatSelectors(deferredState),
    [deferredState],
  )

  return (
    <FakeChatStateContext.Provider value={deferredState}>
      <FakeChatSelectorsContext.Provider value={deferredSelectors}>
        {children}
      </FakeChatSelectorsContext.Provider>
    </FakeChatStateContext.Provider>
  )
}

/**
 * Returns the fake chat state object.
 * Must be used within a FakeChatProvider component.
 * 
 * @throws {Error} When used outside of FakeChatProvider
 */
export function useFakeChatState(): FakeChatState {
  const context = useContext(FakeChatStateContext)
  if (!context) {
    throw new Error('useFakeChatState must be used within a FakeChatProvider')
  }
  return context
}

/**
 * Returns memoized action dispatchers for manipulating fake chat state.
 * All actions are type-safe and handle state mutations immutably.
 * 
 * @throws {Error} When used outside of FakeChatProvider
 */
export function useFakeChatActions(): {
  setPlatform: (platform: ChatPlatform) => void
  updateMetadata: (changes: Partial<ConversationMetadata>) => void
  addMessage: (message: ChatMessage) => void
  updateMessage: (id: string, changes: Partial<ChatMessage>) => void
  removeMessage: (id: string) => void
  setActiveMessage: (messageId: string | null) => void
  addParticipant: (participant: ChatParticipant) => void
  updateParticipant: (id: string, changes: Partial<ChatParticipant>) => void
  removeParticipant: (id: string) => void
  resetConversation: () => void
  setExporting: (isExporting: boolean) => void
  setError: (message: string | null) => void
  generateMessageId: () => string
  generateParticipantId: () => string
  toggleSidebar: () => void
  addReaction: (messageId: string, emoji: string) => void
  removeReaction: (messageId: string, emoji: string) => void
  toggleReaction: (messageId: string, emoji: string) => void
  updateMessageStatus: (id: string, status: MessageStatus) => void
} {
  const dispatch = useContext(FakeChatDispatchContext)
  if (!dispatch) {
    throw new Error('useFakeChatActions must be used within a FakeChatProvider')
  }
  // Initial preset (set by landing-page variants via the Provider's
  // props). Consumed by resetConversation below so the reset path
  // honours the variant's advertised platform rather than falling
  // back to the umbrella default.
  const initialPreset = useContext(FakeChatInitialPresetContext)

  return useMemo(
    () => ({
      /** Sets the chat platform and applies platform-specific styling */
      setPlatform: (platform: ChatPlatform) => dispatch({ type: 'SET_PLATFORM', payload: platform }),
      /** Updates conversation metadata with partial changes */
      updateMetadata: (changes: Partial<ConversationMetadata>) => dispatch({ type: 'UPDATE_METADATA', payload: changes }),
      /** Adds a new message to the conversation and sets it as active */
      addMessage: (message: ChatMessage) => dispatch({ type: 'ADD_MESSAGE', payload: message }),
      /** Updates an existing message with partial changes */
      updateMessage: (id: string, changes: Partial<ChatMessage>) => dispatch({ type: 'UPDATE_MESSAGE', payload: { id, changes } }),
      /** Removes a message from the conversation */
      removeMessage: (id: string) => dispatch({ type: 'REMOVE_MESSAGE', payload: id }),
      /** Sets the currently active/selected message */
      setActiveMessage: (messageId: string | null) => dispatch({ type: 'SET_ACTIVE_MESSAGE', payload: messageId }),
      /** Adds a new participant to the conversation */
      addParticipant: (participant: ChatParticipant) => dispatch({ type: 'ADD_PARTICIPANT', payload: participant }),
      /** Updates an existing participant with partial changes */
      updateParticipant: (id: string, changes: Partial<ChatParticipant>) =>
        dispatch({ type: 'UPDATE_PARTICIPANT', payload: { id, changes } }),
      /** Removes a participant and all their messages from the conversation */
      removeParticipant: (id: string) => dispatch({ type: 'REMOVE_PARTICIPANT', payload: id }),
      /**
       * Resets the conversation to its initial state. On landing-
       * page variants (iMessage / Slack / group-chat), the
       * Provider-supplied `initialPreset` is forwarded as the
       * action payload so the reset reapplies the variant's
       * platform + channelType — preserving the platform the URL
       * promised the user.
       */
      resetConversation: () => dispatch({
        type: 'RESET_CONVERSATION',
        payload: {
          platform: initialPreset.platform,
          channelType: initialPreset.channelType,
        },
      }),
      /**
       * Sets the export loading state. Consumed by
       * `FakeChatControls.handleExport`, which captures the preview
       * with html2canvas and downloads a PNG (mirroring
       * SocialMediaMockupGenerator.handleExport /
       * EmailThreadGenerator.downloadPNG).
       */
      setExporting: (isExporting: boolean) => dispatch({ type: 'SET_EXPORTING', payload: isExporting }),
      /** Sets an error message or clears it */
      setError: (message: string | null) => dispatch({ type: 'SET_ERROR', payload: message }),
      /** Generates a unique ID for new messages */
      generateMessageId: () => createId(),
      /** Generates a unique ID for new participants */
      generateParticipantId: () => createId(),
      /** Toggles the Slack sidebar collapsed state */
      toggleSidebar: () => dispatch({ type: 'TOGGLE_SIDEBAR' }),
      /** Adds a reaction to a message */
      addReaction: (messageId: string, emoji: string) => dispatch({ type: 'ADD_REACTION', payload: { messageId, emoji } }),
      /** Removes a reaction from a message */
      removeReaction: (messageId: string, emoji: string) => dispatch({ type: 'REMOVE_REACTION', payload: { messageId, emoji } }),
      /** Toggles the viewer's reaction; the reducer decides add vs remove from live state. */
      toggleReaction: (messageId: string, emoji: string) => dispatch({ type: 'TOGGLE_REACTION', payload: { messageId, emoji } }),
      /** Updates the delivery status of a message */
      updateMessageStatus: (id: string, status: MessageStatus) => dispatch({ type: 'UPDATE_MESSAGE_STATUS', payload: { id, status } })
    }),
    // initialPreset is memoised on the Provider side, so adding it
    // here doesn't cause action-function churn on Provider re-renders.
    [dispatch, initialPreset]
  )
}

/**
 * Exposes memoized selector helpers derived from fake chat state.
 * Provides optimized access to computed state values.
 * 
 * @throws {Error} When used outside of FakeChatProvider
 */
export function useFakeChatSelectors(): FakeChatSelectors {
  const selectors = useContext(FakeChatSelectorsContext)
  if (!selectors) {
    throw new Error('useFakeChatSelectors must be used within a FakeChatProvider')
  }
  return selectors
}
